import React, { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Eye, X, Info, Loader2, RefreshCw, ArrowLeft } from "lucide-react";
import { useAuth } from "../lib/auth";

const STATUSES = ["trialing", "active", "past_due", "suspended"];
const PAYMENT_STATUSES = ["paid", "unpaid", "pending", "refunded", "canceled", "failed", "expired", "no_payment_required"];

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

export default function PlatformOrgs() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination for orgs listing — 10 rows per view to keep the page snappy
  // once the number of tenant orgs grows into the hundreds (Iter42).
  const ORGS_PAGE_SIZE = 10;
  const [orgsPage, setOrgsPage] = useState(0);
  const [detailsId, setDetailsId] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Per-user activity drawer (login history + password events).
  const [activityForId, setActivityForId] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityQuery, setActivityQuery] = useState("");

  const openActivity = async (userId) => {
    setActivityForId(userId);
    setActivity(null);
    setActivityQuery("");
    setActivityLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}/activity`, { params: { limit: 100 } });
      setActivity(r.data);
    } catch (err) {
      toast.error(apiError(err, "Failed to load activity log"));
      setActivityForId(null);
    } finally {
      setActivityLoading(false);
    }
  };

  const closeActivity = () => {
    setActivityForId(null);
    setActivity(null);
    setActivityQuery("");
  };

  const filteredActivityEvents = React.useMemo(() => {
    if (!activity?.events) return [];
    const q = activityQuery.trim().toLowerCase();
    if (!q) return activity.events;
    return activity.events.filter((e) => {
      const hay = [e.event, e.ip, e.user_agent, e.email]
        .map((x) => (x || "").toString().toLowerCase())
        .join(" \n ");
      return hay.includes(q);
    });
  }, [activity, activityQuery]);

  const openDetails = async (orgId) => {
    setDetailsId(orgId);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const r = await api.get(`/admin/orgs/${orgId}/details`);
      setDetails(r.data);
    } catch (e) {
      toast.error(apiError(e, "Could not load details"));
      setDetailsId(null);
    } finally {
      setDetailsLoading(false);
    }
  };
  const closeDetails = () => { setDetailsId(null); setDetails(null); };

  // Inline payment-status override state.
  const [paymentDraft, setPaymentDraft] = useState({});
  const [savingPayment, setSavingPayment] = useState(null);
  const [reprocessingId, setReprocessingId] = useState(null);

  const savePaymentStatus = async (session_id, next_status) => {
    if (!session_id || !next_status) return;
    setSavingPayment(session_id);
    try {
      await api.patch(`/admin/payments/${session_id}`, { payment_status: next_status });
      toast.success(`Payment status set to ${next_status}`);
      if (detailsId) {
        const r = await api.get(`/admin/orgs/${detailsId}/details`);
        setDetails(r.data);
      }
      setPaymentDraft((d) => { const n = { ...d }; delete n[session_id]; return n; });
    } catch (e) {
      toast.error(apiError(e, "Could not update payment status"));
    } finally {
      setSavingPayment(null);
    }
  };

  const reprocessPayment = async (session_id) => {
    if (!session_id) return;
    setReprocessingId(session_id);
    try {
      const r = await api.post(`/admin/payment-transactions/${session_id}/reprocess`);
      if (r.data.changed) {
        toast.success(`Reprocessed — status is now ${r.data.payment_status}`);
      } else {
        toast.message(`Stripe reports ${r.data.payment_status} — no change needed`);
      }
      if (detailsId) {
        const dr = await api.get(`/admin/orgs/${detailsId}/details`);
        setDetails(dr.data);
      }
    } catch (e) {
      toast.error(apiError(e, "Could not reprocess payment"));
    } finally {
      setReprocessingId(null);
    }
  };

  const [orphans, setOrphans] = useState(null);
  const [deletingOrphanId, setDeletingOrphanId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, oRes, orphRes] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/orgs"),
        api.get("/admin/orphans").catch((err) => {
          if (err?.response?.status && err.response.status !== 403) {
            console.warn("orphans fetch failed:", err.response?.status);
          }
          return { data: null };
        }),
      ]);
      setPlans(pRes.data);
      setOrgs(oRes.data);
      // Clamp pagination back if the list got shorter after a delete/refresh.
      setOrgsPage((p) => {
        const maxPage = Math.max(0, Math.ceil((oRes.data || []).length / ORGS_PAGE_SIZE) - 1);
        return Math.min(p, maxPage);
      });
      setOrphans(orphRes.data || null);
    } catch (e) {
      toast.error(apiError(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  const deleteOrphanProfile = async (pid, label) => {
    if (!window.confirm(`Delete orphan profile "${label}"?\n\nThis removes the profile and any calibrations attached to it. It cannot be undone.`)) return;
    setDeletingOrphanId(pid);
    try {
      await api.delete(`/admin/orphans/profiles/${pid}`);
      toast.success("Orphan profile deleted");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Failed to delete orphan"));
    } finally {
      setDeletingOrphanId(null);
    }
  };

  useEffect(() => { load(); }, []);

  const setOrgPlan = async (orgId, planId) => {
    try {
      await api.put(`/admin/orgs/${orgId}/plan`, { plan_id: planId });
      toast.success("Plan changed");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not change plan"));
    }
  };

  const setOrgStatus = async (orgId, status) => {
    try {
      await api.put(`/admin/orgs/${orgId}/status`, { status });
      toast.success("Status changed");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not change status"));
    }
  };

  const removeOrg = async (orgId, name) => {
    if (!window.confirm(`Permanently delete ${name} and ALL its data?`)) return;
    try {
      await api.delete(`/admin/orgs/${orgId}`);
      toast.success("Org deleted");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  if (!user?.is_platform_owner) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="platform-orgs-forbidden">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[FORBIDDEN]</div>
        <h1 className="font-display font-bold text-3xl">Platform owner access required</h1>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="platform-orgs-page">
      <div className="mb-8">
        <a href="/admin/platform" className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] hover:text-[#FF4F00] inline-flex items-center gap-1" data-testid="back-to-platform">
          <ArrowLeft size={12} /> Platform admin
        </a>
      </div>
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00] mb-3">[ GOD MODE ]</div>
      <h1 className="font-display font-bold text-5xl tracking-tight mb-2">Organizations</h1>
      <p className="text-[#666] mb-10">Govern every tenant, change plans, resolve orphan data.</p>

      {loading ? <div className="font-mono-cal text-xs">[LOADING…]</div> : (
        <section data-testid="orgs-section">
          <h2 className="font-display font-bold text-2xl mb-4">Organizations ({orgs.length})</h2>

          {orphans && (
            <div
              className={`cal-card p-4 mb-4 flex items-start gap-4 flex-wrap ${orphans.profiles_count > 0 ? "border-2 border-[#FF4F00] bg-[#FFF5F0]" : ""}`}
              data-testid="employee-totals-summary"
            >
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">
                  [ EMPLOYEE TOTALS ]
                </div>
                <div className="font-display font-semibold">
                  <span data-testid="employee-total-attributed">
                    <b>{orgs.reduce((n, o) => n + (o.usage?.employees || 0), 0)}</b> attributed to a live org
                  </span>
                  {" · "}
                  <span className={orphans.profiles_count > 0 ? "text-[#FF4F00]" : "text-[#666]"} data-testid="employee-total-orphan">
                    <b>{orphans.profiles_count}</b> orphaned
                  </span>
                  {" · "}
                  <span className="text-[#666]" data-testid="employee-total-raw">
                    <b>{orphans.profiles_total}</b> in the profiles collection
                  </span>
                </div>
                {orphans.profiles_count > 0 && (
                  <div className="text-xs text-[#666] mt-1">
                    Orphan profiles have an <code className="font-mono-cal">org_id</code> that no longer resolves to an active organization
                    (usually because the org was hard-deleted). They&apos;re invisible to per-org counts. Review and delete below.
                  </div>
                )}
              </div>
            </div>
          )}

          {orphans && orphans.profiles_count > 0 && (
            <details className="cal-card p-4 mb-4" data-testid="orphan-profiles-panel" open>
              <summary className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] cursor-pointer select-none">
                [ ORPHAN PROFILES · {orphans.profiles_count} ]
              </summary>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead className="font-mono-cal text-[10px] uppercase text-[#666]">
                    <tr>
                      <th className="text-left p-2">Employee</th>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Stale org_id</th>
                      <th className="text-left p-2">Created</th>
                      <th className="text-right p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orphans.profiles.map((p) => (
                      <tr key={p.id} className="border-t border-[#111]/10" data-testid={`orphan-profile-row-${p.id}`}>
                        <td className="p-2 font-display font-bold">{p.employee_name || "—"}</td>
                        <td className="p-2 font-mono-cal text-xs">{p.employee_email || "—"}</td>
                        <td className="p-2 font-mono-cal text-[10px] text-[#666]">{(p.org_id || "").slice(0, 8) || "(none)"}…</td>
                        <td className="p-2 font-mono-cal text-xs text-[#666]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => deleteOrphanProfile(p.id, p.employee_name || p.employee_email || p.id)}
                            disabled={deletingOrphanId === p.id}
                            className="cal-btn cal-btn-ghost text-xs text-[#B00020]"
                            data-testid={`orphan-delete-${p.id}`}
                          >
                            {deletingOrphanId === p.id ? "Deleting…" : <><Trash2 size={12} className="inline mr-1" /> Delete</>}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Limit to 10 rows per page to speed up window load time (Iter42) */}
          <div className="overflow-x-auto cal-card">
            <table className="w-full text-sm">
              <thead className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Domain</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Status</th><th className="text-right p-3">Mgrs</th><th className="text-right p-3">Empls</th><th className="text-right p-3">Action</th></tr>
              </thead>
              <tbody>
                {orgs.slice(orgsPage * ORGS_PAGE_SIZE, (orgsPage + 1) * ORGS_PAGE_SIZE).map((o) => (
                  <tr key={o.id} className="border-t border-[#111]/10" data-testid={`org-row-${o.id}`}>
                    <td className="p-3 font-display font-bold">{o.name}</td>
                    <td className="p-3 font-mono-cal text-xs text-[#666]">{o.domain || "—"}</td>
                    <td className="p-3">
                      <select value={o.plan_id || ""} onChange={(e) => setOrgPlan(o.id, e.target.value)} className="cal-input text-sm" data-testid={`org-plan-${o.id}`}>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </td>
                    <td className="p-3">
                      <select value={o.status} onChange={(e) => setOrgStatus(o.id, e.target.value)} className="cal-input text-sm" data-testid={`org-status-${o.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-right">{o.usage?.managers}</td>
                    <td className="p-3 text-right">{o.usage?.employees}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => openDetails(o.id)} className="font-mono-cal text-xs underline" data-testid={`org-view-${o.id}`} title="View registration & admin details"><Eye size={14} className="inline mr-1"/>View</button>
                        <button onClick={() => removeOrg(o.id, o.name)} className="text-[#FF4F00]" data-testid={`org-delete-${o.id}`} title="Delete org"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orgs.length > ORGS_PAGE_SIZE && (
              <div
                className="flex items-center justify-between gap-3 border-t border-[#111] bg-[#FAFAF5] px-4 py-3 font-mono-cal text-[11px]"
                data-testid="orgs-pagination"
              >
                <span className="text-[#666] uppercase tracking-widest" data-testid="orgs-pagination-info">
                  Showing {orgsPage * ORGS_PAGE_SIZE + 1}–
                  {Math.min((orgsPage + 1) * ORGS_PAGE_SIZE, orgs.length)}
                  {" "}of {orgs.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOrgsPage((p) => Math.max(0, p - 1))}
                    disabled={orgsPage === 0}
                    className="cal-btn cal-btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="orgs-page-prev"
                  >← Prev</button>
                  <span className="text-[#111]" data-testid="orgs-page-indicator">
                    {orgsPage + 1} / {Math.max(1, Math.ceil(orgs.length / ORGS_PAGE_SIZE))}
                  </span>
                  <button
                    onClick={() => setOrgsPage((p) =>
                      Math.min(Math.ceil(orgs.length / ORGS_PAGE_SIZE) - 1, p + 1))}
                    disabled={(orgsPage + 1) * ORGS_PAGE_SIZE >= orgs.length}
                    className="cal-btn cal-btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="orgs-page-next"
                  >Next →</button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {detailsId && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-6 overflow-y-auto"
          onClick={closeDetails}
          data-testid="org-details-modal"
        >
          <div
            className="bg-[#F4F4F0] border border-[#111] cal-shadow w-full max-w-4xl my-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#111] px-6 py-4">
              <div>
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ORG DETAILS]</div>
                <div className="font-display font-bold text-2xl">{details?.org?.name || "Loading…"}</div>
              </div>
              <button onClick={closeDetails} className="cal-btn cal-btn-ghost" data-testid="org-details-close"><X size={14}/> Close</button>
            </div>

            {detailsLoading || !details ? (
              <div className="p-10 font-mono-cal text-xs text-[#666]">[LOADING…]</div>
            ) : (
              <div className="p-6 space-y-8">
                <section data-testid="org-details-summary">
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[SUMMARY]</div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <div><span className="text-[#666]">Org ID</span> <span className="font-mono-cal text-xs">{details.org.id}</span></div>
                    <div><span className="text-[#666]">Domain</span> {details.org.domain || <span className="font-mono-cal text-xs text-[#999]">(none — created via free-mail)</span>}</div>
                    <div><span className="text-[#666]">Plan</span> <strong>{details.plan?.name || "—"}</strong> · A${details.plan?.monthly_cost ?? 0}/mo</div>
                    <div><span className="text-[#666]">Status</span> <span className="uppercase font-mono-cal text-xs">{details.org.status}</span></div>
                    <div><span className="text-[#666]">Created</span> {fmtDate(details.org.created_at)}</div>
                    <div><span className="text-[#666]">Trial ends</span> {fmtDate(details.org.trial_ends_at)}</div>
                    <div><span className="text-[#666]">Subscription ends</span> {fmtDate(details.org.subscription_period_end)}</div>
                    <div><span className="text-[#666]">Last payment</span> {fmtDate(details.org.last_payment_at)}</div>
                    <div><span className="text-[#666]">Join code</span> <code className="font-mono-cal bg-[#111] text-white px-2 py-0.5">{details.org.join_code}</code></div>
                    <div><span className="text-[#666]">Auth-mgr count</span> {details.org.authorized_manager_count ?? 0}</div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                    <div className="cal-card p-3 text-center"><div className="font-mono-cal text-[10px] text-[#666]">PROFILES</div><div className="font-display font-bold text-2xl">{details.stats.profiles}</div></div>
                    <div className="cal-card p-3 text-center"><div className="font-mono-cal text-[10px] text-[#666]">CALIBRATIONS</div><div className="font-display font-bold text-2xl">{details.stats.calibrations}</div></div>
                    <div className="cal-card p-3 text-center"><div className="font-mono-cal text-[10px] text-[#666]">MEMBERS</div><div className="font-display font-bold text-2xl">{details.stats.members}</div></div>
                  </div>
                </section>

                <section data-testid="org-details-admins">
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[ADMINS] ({details.admins.length})</div>
                  {details.admins.length === 0 ? (
                    <div className="text-sm text-[#666]">No org admins.</div>
                  ) : (
                    <div className="space-y-2">
                      {details.admins.map((a) => (
                        <div key={a.id} className="cal-card p-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`org-admin-row-${a.id}`}>
                          <div>
                            <div className="font-display font-bold">{a.name}</div>
                            <div className="text-[#666]">{a.email}</div>
                          </div>
                          <div className="font-mono-cal text-[10px] text-[#666]">
                            joined {fmtDate(a.created_at)}
                            {a.is_platform_owner && <span className="ml-3 text-[#00A36C]">[PLATFORM]</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section data-testid="org-details-members">
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[ALL MEMBERS]</div>
                  <div className="overflow-x-auto cal-card">
                    <table className="w-full text-sm">
                      <thead className="font-mono-cal text-[10px] uppercase text-[#666]">
                        <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Email</th><th className="text-left p-2">Status</th><th className="text-left p-2">Registered</th><th className="text-left p-2">Last login</th><th className="text-left p-2">Approved by</th><th className="text-right p-2">Activity</th></tr>
                      </thead>
                      <tbody>
                        {details.members.map((m) => (
                          <tr key={m.id} className="border-t border-[#111]/10" data-testid={`org-member-row-${m.id}`}>
                            <td className="p-2 font-display font-bold">{m.name}</td>
                            <td className="p-2 font-mono-cal text-xs">{m.email}</td>
                            <td className="p-2 font-mono-cal text-[10px] uppercase">
                              {m.is_org_admin ? <span className="text-[#FF4F00]">ADMIN · </span> : null}
                              {m.is_authorized ? <span className="text-[#00A36C]">AUTHORIZED</span> : <span className="text-[#FF4F00]">PENDING</span>}
                            </td>
                            <td className="p-2 font-mono-cal text-xs text-[#666]">{fmtDate(m.created_at)}</td>
                            <td className="p-2 font-mono-cal text-xs text-[#666]">{m.last_login_at ? fmtDate(m.last_login_at) : <span className="text-[#999]">never</span>}</td>
                            <td className="p-2 font-mono-cal text-xs text-[#666]">
                              {m.approved_at ? `${fmtDate(m.approved_at)}${m.approved_by ? ` · ${m.approved_by.slice(0,8)}…` : ""}` : "—"}
                            </td>
                            <td className="p-2 text-right">
                              <button
                                type="button"
                                onClick={() => openActivity(m.id)}
                                className="p-1.5 border border-[#111] hover:bg-[#111] hover:text-white transition"
                                title="View activity log"
                                aria-label={`View activity log for ${m.name}`}
                                data-testid={`member-activity-btn-${m.id}`}
                              >
                                <Info size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {details.payments.length > 0 && (
                  <section data-testid="org-details-payments">
                    <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[RECENT PAYMENTS]</div>
                    <div className="overflow-x-auto cal-card">
                      <table className="w-full text-sm">
                        <thead className="font-mono-cal text-[10px] uppercase text-[#666]">
                          <tr>
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Amount</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-left p-2">By</th>
                            <th className="text-left p-2">Override</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.payments.map((p) => {
                            const draft = paymentDraft[p.session_id] ?? p.payment_status;
                            const dirty = draft !== p.payment_status;
                            const busy = savingPayment === p.session_id;
                            return (
                              <tr key={p.session_id} className="border-t border-[#111]/10" data-testid={`payment-row-${p.session_id}`}>
                                <td className="p-2 font-mono-cal text-xs">{fmtDate(p.created_at)}</td>
                                <td className="p-2">${p.amount?.toFixed?.(2) ?? p.amount} {(p.currency || "").toUpperCase()}</td>
                                <td className="p-2 font-mono-cal text-[10px] uppercase" data-testid={`payment-status-${p.session_id}`}>
                                  {p.payment_status === "paid"
                                    ? <span className="text-[#00A36C]">PAID</span>
                                    : <span className="text-[#FF4F00]">{p.payment_status}</span>}
                                  {p.manual_override_at && (
                                    <span
                                      className="ml-2 inline-block px-1.5 py-0.5 text-[9px] tracking-widest bg-[#111] text-white"
                                      title={`Overridden ${fmtDate(p.manual_override_at)} by ${p.manual_override_by || "operator"}`}
                                      data-testid={`payment-manual-badge-${p.session_id}`}
                                    >
                                      MANUAL
                                    </span>
                                  )}
                                </td>
                                <td className="p-2 font-mono-cal text-xs">{p.user_email || "—"}</td>
                                <td className="p-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => reprocessPayment(p.session_id)}
                                      disabled={reprocessingId === p.session_id || busy}
                                      className="cal-btn cal-btn-ghost text-xs py-1"
                                      title="Re-fetch this session from Stripe and heal the local record if Stripe now reports it paid"
                                      data-testid={`payment-reprocess-${p.session_id}`}
                                    >
                                      {reprocessingId === p.session_id
                                        ? <><Loader2 size={12} className="inline animate-spin" /> Re-fetching…</>
                                        : <><RefreshCw size={12} className="inline" /> Reprocess</>}
                                    </button>
                                    <select
                                      value={draft}
                                      onChange={(e) => setPaymentDraft((d) => ({ ...d, [p.session_id]: e.target.value }))}
                                      disabled={busy}
                                      className="cal-input text-xs py-1 min-w-[140px]"
                                      data-testid={`payment-status-select-${p.session_id}`}
                                    >
                                      {PAYMENT_STATUSES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={() => savePaymentStatus(p.session_id, draft)}
                                      disabled={!dirty || busy}
                                      className="cal-btn cal-btn-ghost text-xs py-1"
                                      data-testid={`payment-status-save-${p.session_id}`}
                                    >
                                      {busy ? "Saving…" : "Save"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-[#666] mt-2">
                      God-mode override: this only rewrites the CALIBRATE-side ledger. It does <b>not</b> touch Stripe — process refunds in Stripe first, then mark them here.
                    </p>
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activityForId && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto"
          data-testid="user-activity-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeActivity(); }}
        >
          <div className="bg-white w-full max-w-3xl my-8 border-2 border-[#111] shadow-xl">
            <div className="p-5 border-b border-[#EEE] flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ USER ACTIVITY LOG ]</div>
                {activity?.user ? (
                  <>
                    <div className="font-display font-bold text-2xl mt-1">{activity.user.name}</div>
                    <div className="text-sm text-[#666] font-mono-cal">{activity.user.email}</div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs font-mono-cal text-[#666]">
                      <div><b className="text-[#111]">Last login:</b> {activity.user.last_login_at ? fmtDate(activity.user.last_login_at) : "never"}</div>
                      <div><b className="text-[#111]">Last IP:</b> {activity.user.last_login_ip || "—"}</div>
                      <div><b className="text-[#111]">Password changed:</b> {activity.user.password_changed_at ? fmtDate(activity.user.password_changed_at) : "—"}</div>
                    </div>
                  </>
                ) : (
                  <div className="font-display font-bold text-2xl mt-1">Loading…</div>
                )}
              </div>
              <button onClick={closeActivity} className="cal-btn cal-btn-ghost" data-testid="user-activity-close" aria-label="Close">
                <X size={14} />
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto" data-testid="user-activity-body">
              {activity?.events?.length > 0 && (
                <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="user-activity-search-row">
                  <input
                    type="text"
                    value={activityQuery}
                    onChange={(e) => setActivityQuery(e.target.value)}
                    placeholder="Search events, IPs, user agents…"
                    className="cal-input flex-1 min-w-[220px]"
                    data-testid="user-activity-search"
                    aria-label="Search activity log"
                  />
                  {activityQuery && (
                    <button
                      type="button"
                      onClick={() => setActivityQuery("")}
                      className="cal-btn cal-btn-ghost text-xs"
                      data-testid="user-activity-search-clear"
                    >
                      <X size={12} className="inline mr-1" /> Clear
                    </button>
                  )}
                  <span className="font-mono-cal text-[11px] text-[#666] whitespace-nowrap" data-testid="user-activity-count">
                    {filteredActivityEvents.length} of {activity.events.length}
                  </span>
                </div>
              )}
              {activityLoading ? (
                <div className="flex items-center gap-2 text-sm text-[#666]">
                  <Loader2 size={14} className="animate-spin" /> Loading activity…
                </div>
              ) : !activity ? null : activity.events.length === 0 ? (
                <div className="cal-card p-6 text-center text-sm text-[#666]" data-testid="user-activity-empty">
                  No recorded activity yet. Historical login events before this feature was enabled aren&apos;t shown.
                </div>
              ) : filteredActivityEvents.length === 0 ? (
                <div className="cal-card p-6 text-center text-sm text-[#666]" data-testid="user-activity-no-matches">
                  No events match &ldquo;{activityQuery}&rdquo;.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="user-activity-table">
                  <thead className="font-mono-cal text-[10px] uppercase text-[#666]">
                    <tr>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Event</th>
                      <th className="text-left p-2">IP</th>
                      <th className="text-left p-2">User agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActivityEvents.map((e, i) => {
                      const isFail = (e.event || "").includes("fail") || (e.event || "").includes("bad_token") || (e.event || "").includes("expired");
                      const isLogin = e.event === "login_success";
                      return (
                        <tr key={i} className="border-t border-[#111]/10" data-testid={`user-activity-row-${i}`}>
                          <td className="p-2 font-mono-cal text-xs whitespace-nowrap">{fmtDate(e.created_at)}</td>
                          <td className={`p-2 font-mono-cal text-[10px] uppercase ${isFail ? "text-[#B00020]" : isLogin ? "text-[#00A36C]" : "text-[#111]"}`}>
                            {e.event || "—"}
                          </td>
                          <td className="p-2 font-mono-cal text-xs">{e.ip || "—"}</td>
                          <td className="p-2 font-mono-cal text-[10px] text-[#666] truncate max-w-[280px]" title={e.user_agent || ""}>
                            {e.user_agent || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t border-[#EEE] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs font-mono-cal text-[#666]">
                Showing up to <b>100</b> most recent events · retained for 90 days.
              </div>
              <button onClick={closeActivity} className="cal-btn cal-btn-ghost" data-testid="user-activity-close-footer">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
